'use client';
import { getApiBase } from '@/lib/api-boundary';




import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { api } from '@/lib/api';

import { formatDateDDMMYYYY } from '@/lib/format-date';

import {

  mockContracts,

  mockContractScopeSystems,

  mockContractTeamAssignments,

  mockContractClientRepresentatives,

  mockContractProjectSites,

  mockContractAdminHistory,

  mockContractRevisions,

  mockContractRevisionChanges,

  mockContractMetrics,

  mockUsersMaster,

  mockProjectSitesMaster,

  DbContract,

  resolveActorInfo,

} from '@/app/components/contracts/mockContracts';



function mapDbContractToHydrated(db: DbContract): HydratedContractDTO {

  const scopeSystems = mockContractScopeSystems

    .filter((s) => s.contract_id === db.id)

    .map((s) => s.system);



  const teamAssignments = mockContractTeamAssignments

    .filter((a) => a.contract_id === db.id)

    .map((a) => {

      const user = mockUsersMaster.find((u) => u.id === a.user_id);

      return {

        assignmentId: a.id,

        role: a.role,

        userId: a.user_id,

        name: user?.name || 'Assigned Representative',

        email: user?.email || '',

        designation: user?.designation || a.role,

        assignedAt: a.assigned_at,

      };

    });



  const clientReps = mockContractClientRepresentatives

    .filter((c) => c.contract_id === db.id)

    .map((c) => {

      const user = mockUsersMaster.find((u) => u.id === c.user_id);

      return {

        assignmentId: c.id,

        userId: c.user_id,

        name: user?.name || 'Client Representative',

        email: user?.email || '',

        designation: user?.designation || 'Client Representative',

        assignedAt: c.assigned_at,

      };

    });



  const linkedSites = mockContractProjectSites

    .filter((s) => s.contract_id === db.id)

    .map((s) => {

      const site = mockProjectSitesMaster.find((m) => m.site_id === s.site_id);

      return {

        siteId: s.site_id,

        siteName: site?.site_name || s.site_id,

        status: site?.status || 'ACTIVE',

      };

    });



  const metrics = mockContractMetrics.find((m) => m.contract_id === db.id) || {

    contract_id: db.id,

    approved_submissions: 0,

    total_submissions: 0,

    completed_sites: 0,

    total_sites: 0,

    valid_equipment: 0,

    total_equipment: 0,

    pending_signatures_total: 0,

    pending_signatures_overdue: 0,

  };



  const revisions = mockContractRevisions.filter((r) => r.contract_id === db.id);

  const revisionHistory = revisions.map((r) => {

    const changes = mockContractRevisionChanges.filter((c) => c.revision_id === r.id);

    const dateParts = r.changed_at.split(' ');

    const date = dateParts[0] || r.changed_at;

    const time = dateParts.slice(1).join(' ') || '10:00 AM';

    const endDateChange = changes.find((c) => c.field === 'planned_end_date');

    const resolved = resolveActorInfo(r.changed_by);



    const formattedChanges = changes.map((ch) => {

      const fieldLabel =

        ch.field === 'planned_end_date'

          ? 'Planned End Date'

          : ch.field === 'name'

            ? 'Contract Title'

            : ch.field;

      const oldVal = ch.field.includes('date')

        ? formatDateDDMMYYYY(ch.old_value)

        : ch.old_value || '—';

      const newVal = ch.field.includes('date') ? formatDateDDMMYYYY(ch.new_value) : ch.new_value;

      return { field: fieldLabel, oldValue: oldVal, newValue: newVal };

    });



    return {

      revision: r.revision_number,

      date,

      time,

      changedBy: resolved.name,

      actorName: resolved.name,

      actorDesignation: resolved.designation,

      userId: resolved.id || undefined,

      actorId: resolved.id || undefined,

      comment: endDateChange

        ? `Schedule extended to ${formatDateDDMMYYYY(endDateChange.new_value)}`

        : 'Contract specifications updated',

      changes: formattedChanges,

      previousPlannedEndDate: endDateChange ? endDateChange.old_value : null,

    };

  });



  const adminHist = mockContractAdminHistory

    .filter((a) => a.contract_id === db.id)

    .map((a) => ({

      id: a.id,

      userId: a.user_id,

      assignedAt: a.assigned_at,

      assignedBy: a.assigned_by,

      transferReason: a.transfer_reason,

      revokedAt: a.revoked_at,

      fullNameSnapshot: a.full_name_snapshot,

      designationSnapshot: a.designation_snapshot,

      emailSnapshot: a.email_snapshot,

    }));



  return {

    id: db.id,

    contractNumber: db.contract_number,

    name: db.name,

    clientName: db.client_name,

    clientPrimaryContactName: db.client_primary_contact_name,

    clientPrimaryContactPhone: db.client_primary_contact_phone,

    clientPrimaryContactEmail: db.client_primary_contact_email,

    contractType: db.contract_type,

    startDate: db.start_date,

    plannedEndDate: db.planned_end_date,

    actualEndDate: db.actual_end_date,

    status: db.status === 'PARTIALLY_CONFIGURED' ? 'ACTIVE' : db.status,

    lastModified: db.last_modified,

    contractValue: db.contract_value,

    contractValueCurrency: db.contract_value_currency,

    retentionPercentage: db.retention_percentage,

    performanceGuarantee: db.performance_guarantee,

    country: db.country,

    state: db.state,

    city: db.city,

    pincode: db.pincode,

    address: db.address,

    organizationId: db.organization_id,

    organizationName: db.organization_name,

    contractAdmin: db.contract_admin_user_id

      ? {

          userId: db.contract_admin_user_id,

          fullName: 'Emma Wilson',

          designation: 'Contract Admin',

          email: 'emma.w@mepify.com',

        }

      : null,

    isDeleted: db.is_deleted,

    deletedAt: null,

    deletedBy: null,

    completedAt: db.completed_at,

    completedBy: db.completed_by,

    archivedAt: db.archived_at,

    archivedBy: db.archived_by,

    archiveReason: db.archive_reason,

    configurationStatus:

      db.status === 'PARTIALLY_CONFIGURED' ? 'PARTIALLY_CONFIGURED' : 'FULLY_CONFIGURED',

    systems: scopeSystems,

    team: teamAssignments,

    clientRepresentatives: clientReps,

    linkedProjectSites: linkedSites,

    contractMetrics: {

      approvedSubmissions: metrics.approved_submissions,

      totalSubmissions: metrics.total_submissions,

      completedSites: metrics.completed_sites,

      totalSites: metrics.total_sites,

      validEquipment: metrics.valid_equipment,

      totalEquipment: metrics.total_equipment,

      pendingSignatures: {

        total: metrics.pending_signatures_total,

        overdue: metrics.pending_signatures_overdue,

      },

    },

    revisionCount: revisions.length,

    revisionHistory,

    timelineRevisionCount: revisionHistory.filter(

      (r) =>

        Boolean(r.previousPlannedEndDate) ||

        (r.changes &&

          r.changes.some(

            (ch) =>

              ch.field === 'Planned End Date' ||

              ch.field === 'Start Date' ||

              ch.field === 'plannedEndDate' ||

              ch.field === 'startDate' ||

              ch.field === 'planned_end_date' ||

              ch.field === 'start_date',

          )),

    ).length,

    timelineRevisionHistory: revisionHistory.filter(

      (r) =>

        Boolean(r.previousPlannedEndDate) ||

        (r.changes &&

          r.changes.some(

            (ch) =>

              ch.field === 'Planned End Date' ||

              ch.field === 'Start Date' ||

              ch.field === 'plannedEndDate' ||

              ch.field === 'startDate' ||

              ch.field === 'planned_end_date' ||

              ch.field === 'start_date',

          )),

    ),

    adminHistory: adminHist,

  };

}



// Re-export type definitions to match backend structure

export interface ContractAdminDTO {

  userId: string;

  fullName: string;

  designation: string;

  email: string;

}



export interface TeamMemberDTO {

  assignmentId: string;

  role: 'PROJECT_DIRECTOR' | 'QA_MANAGER';

  userId: string;

  name: string;

  email: string;

  designation: string;

  assignedAt: string;

}



export interface ClientRepresentativeDTO {

  assignmentId: string;

  userId: string;

  name: string;

  email: string;

  designation: string;

  assignedAt: string;

}



export interface LinkedProjectSiteDTO {

  siteId: string;

  siteName: string;

  status: 'ACTIVE' | 'COMPLETED' | 'ARCHIVED';

}



export interface RevisionChangeDTO {

  field: string;

  oldValue: string;

  newValue: string;

}



export interface RevisionHistoryDTO {

  id?: string;

  revision: number;

  date: string;

  time: string;

  changedBy: string;

  actorName?: string;

  actorDesignation?: string;

  actorId?: string;

  comment: string;

  changes?: RevisionChangeDTO[];

  userId?: string;

  previousPlannedEndDate?: string | null;

  timestamp?: number;

  changedAt?: string;

}



export interface AdminHistoryDTO {

  id: string;

  userId: string;

  assignedAt: string;

  assignedBy: string | null;

  transferReason: string | null;

  revokedAt: string | null;

  fullNameSnapshot: string;

  designationSnapshot: string;

  emailSnapshot: string;

}





export interface HydratedContractDTO {
  id: string;
  tenantId?: string;
  contractNumber: string;

  name: string;

  clientName: string;

  clientPrimaryContactName: string | null;

  clientPrimaryContactPhone: string | null;

  clientPrimaryContactEmail: string | null;

  contractType: string;

  startDate: string;

  plannedEndDate: string;

  actualEndDate: string | null;

  status: 'ACTIVE' | 'COMPLETED' | 'ARCHIVED';

  lastModified: string;

  contractValue: number;

  contractValueCurrency: string;

  retentionPercentage: number;

  performanceGuarantee: number;

  country: string;

  state: string;

  city: string;

  pincode: string;

  address: string;

  organizationId: string;

  organizationName: string;

  contractAdmin: ContractAdminDTO | null;

  isDeleted: boolean;

  deletedAt: string | null;

  deletedBy: string | null;

  completedAt: string | null;

  completedBy: string | null;

  archivedAt: string | null;

  archivedBy: string | null;

  archiveReason: string | null;

  configurationStatus: 'FULLY_CONFIGURED' | 'PARTIALLY_CONFIGURED';

  systems: import('@/app/components/contracts/mockContracts').ScopeSystem[];

  team: TeamMemberDTO[];

  clientRepresentatives: ClientRepresentativeDTO[];

  linkedProjectSites: LinkedProjectSiteDTO[];

  contractMetrics: {

    approvedSubmissions: number;

    totalSubmissions: number;

    completedSites: number;

    totalSites: number;

    validEquipment: number;

    totalEquipment: number;

    pendingSignatures: {

      total: number;

      overdue: number;

    };

    overallCompletion?: number;

  };

  revisionCount: number;

  revisionHistory: RevisionHistoryDTO[];

  timelineRevisionCount?: number;

  timelineRevisionHistory?: RevisionHistoryDTO[];

  adminHistory: AdminHistoryDTO[];

}



export function useContracts(options?: { isDemo?: boolean }) {

  const { data, isLoading, refetch, error } = useQuery<{ contracts: HydratedContractDTO[] }>({

    queryKey: ['contracts', options?.isDemo, getApiBase()],

    queryFn: async () => {

      const isDemoMode =

        options?.isDemo ??

        (typeof window !== 'undefined' &&

          (new URLSearchParams(window.location.search).get('demo') === 'true' ||

            new URLSearchParams(window.location.search).get('mock') === 'true'));



      if (isDemoMode) {

        return { contracts: mockContracts.map(mapDbContractToHydrated) };

      }



      const BASE_URL =

        getApiBase();

      const response = await fetch(`${BASE_URL}/api/v1/contracts`);
      if (!response.ok) throw new Error('Unable to load contracts (HTTP ' + response.status + ')');
      const result = await response.json();
      if (!Array.isArray(result?.contracts)) throw new Error('Invalid contracts response');
      return result;

    },

  });



  return {

    contracts: data?.contracts ?? [],

    isLoading,

    refetch,

    error,

  };

}



export function useContract(id: string, options?: { isDemo?: boolean }) {

  const { data, isLoading, refetch, error } = useQuery<{ contract: HydratedContractDTO }>({

    queryKey: ['contract', id, options?.isDemo],

    queryFn: async () => {

      const isDemoMode =

        options?.isDemo ??

        (typeof window !== 'undefined' &&

          (new URLSearchParams(window.location.search).get('demo') === 'true' ||

            new URLSearchParams(window.location.search).get('mock') === 'true'));



      if (isDemoMode) {

        const match = mockContracts.find((c) => c.id === id) || mockContracts[0];

        return { contract: mapDbContractToHydrated(match) };

      }



      const BASE_URL =

        getApiBase();

      try {

        const response = await fetch(`${BASE_URL}/api/v1/contracts/${id}`);

        if (response.ok) {

          const res = await response.json();

          if (res && res.contract) {

            return res;

          }

        }

        console.warn(

          `Fetch contract ${id} failed with status ${response.status}, using mock fallback`,

        );

      } catch (err) {

        console.warn(`Fetch contract ${id} encountered error, using mock fallback:`, err);

      }

      const match = mockContracts.find((c) => c.id === id) || mockContracts[0];

      return { contract: mapDbContractToHydrated(match) };

    },

    enabled: !!id,

  });



  return {

    contract: data?.contract ?? null,

    isLoading,

    refetch,

    error,

  };

}



export function useCreateContract() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: any) => {
      const headers: Record<string, string> = {};
      if (payload.tenantId) {
        headers['x-ops-tenant-id'] = payload.tenantId;
      }
      return api.post<{ contract: HydratedContractDTO }>('/api/v1/contracts', payload, { headers });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contracts'] });
    },
  });
}



export function useUpdateContract() {

  const queryClient = useQueryClient();



  // Patch details

  const updateDetails = useMutation({

    mutationFn: ({ id, payload }: { id: string; payload: any }) =>

      api.patch<{ contract: HydratedContractDTO }>(`/api/v1/contracts/${id}`, payload),

    onSuccess: (_, variables) => {

      queryClient.invalidateQueries({ queryKey: ['contracts'] });

      queryClient.invalidateQueries({ queryKey: ['contract', variables.id] });

    },

  });



  // Transfer admin owner

  const transferAdmin = useMutation({

    mutationFn: ({

      id,

      newAdminUserId,

      transferReason,

    }: {

      id: string;

      newAdminUserId: string | null;

      transferReason?: string;

    }) =>

      api.post<{ contract: HydratedContractDTO }>(`/api/v1/contracts/${id}/admin`, {

        newAdminUserId,

        transferReason,

      }),

    onSuccess: (_, variables) => {

      queryClient.invalidateQueries({ queryKey: ['contracts'] });

      queryClient.invalidateQueries({ queryKey: ['contract', variables.id] });

    },

  });



  // Update Project Directors

  const updateDirectors = useMutation({

    mutationFn: ({ id, userIds }: { id: string; userIds: string[] }) =>

      api.post<{ contract: HydratedContractDTO }>(`/api/v1/contracts/${id}/directors`, { userIds }),

    onSuccess: (_, variables) => {

      queryClient.invalidateQueries({ queryKey: ['contracts'] });

      queryClient.invalidateQueries({ queryKey: ['contract', variables.id] });

    },

  });



  // Update QA Managers

  const updateQA = useMutation({

    mutationFn: ({ id, userIds }: { id: string; userIds: string[] }) =>

      api.post<{ contract: HydratedContractDTO }>(`/api/v1/contracts/${id}/qa`, { userIds }),

    onSuccess: (_, variables) => {

      queryClient.invalidateQueries({ queryKey: ['contracts'] });

      queryClient.invalidateQueries({ queryKey: ['contract', variables.id] });

    },

  });



  // Update Client Representatives

  const updateClientReps = useMutation({

    mutationFn: ({ id, userIds }: { id: string; userIds: string[] }) =>

      api.post<{ contract: HydratedContractDTO }>(`/api/v1/contracts/${id}/client-reps`, {

        userIds,

      }),

    onSuccess: (_, variables) => {

      queryClient.invalidateQueries({ queryKey: ['contracts'] });

      queryClient.invalidateQueries({ queryKey: ['contract', variables.id] });

    },

  });



  // Complete

  const complete = useMutation({

    mutationFn: (id: string) =>

      api.post<{ contract: HydratedContractDTO }>(`/api/v1/contracts/${id}/complete`, {}),

    onSuccess: (_, id) => {

      queryClient.invalidateQueries({ queryKey: ['contracts'] });

      queryClient.invalidateQueries({ queryKey: ['contract', id] });

    },

  });



  // Archive

  const archive = useMutation({

    mutationFn: ({ id, archiveReason }: { id: string; archiveReason: string }) =>

      api.post<{ contract: HydratedContractDTO }>(`/api/v1/contracts/${id}/archive`, {

        archiveReason,

      }),

    onSuccess: (_, variables) => {

      queryClient.invalidateQueries({ queryKey: ['contracts'] });

      queryClient.invalidateQueries({ queryKey: ['contract', variables.id] });

    },

  });



  // Delete

  const deleteContract = useMutation({

    mutationFn: (id: string) => api.del<{ success: boolean }>(`/api/v1/contracts/${id}`),

    onSuccess: () => {

      queryClient.invalidateQueries({ queryKey: ['contracts'] });

    },

  });



  return {

    updateDetails,

    transferAdmin,

    updateDirectors,

    updateQA,

    updateClientReps,

    complete,

    archive,

    deleteContract,

  };

}



export function useContractTeam(contractId: string, options?: { isDemo?: boolean }) {

  const { data, isLoading, refetch, error } = useQuery<{

    contractId: string;

    team: Record<string, any[]>;

  }>({

    queryKey: ['contractTeam', contractId, options?.isDemo],

    queryFn: async () => {

      const isDemoMode =

        options?.isDemo ??

        (typeof window !== 'undefined' &&

          (new URLSearchParams(window.location.search).get('demo') === 'true' ||

            new URLSearchParams(window.location.search).get('mock') === 'true'));



      if (isDemoMode) {

        const teamAssignments = mockContractTeamAssignments

          .filter((a) => a.contract_id === contractId)

          .map((a) => {

            const user = mockUsersMaster.find((u) => u.id === a.user_id);

            return {

              id: a.id,

              userId: a.user_id,

              name: user?.name || 'Assigned Member',

              email: user?.email || '',

              role: a.role,

              stakeholderRole: 'MAIN_CONTRACTOR',

              designation: user?.designation || a.role,

              assignedAt: a.assigned_at,

            };

          });



        const clientReps = mockContractClientRepresentatives

          .filter((c) => c.contract_id === contractId)

          .map((c) => {

            const user = mockUsersMaster.find((u) => u.id === c.user_id);

            return {

              id: c.id,

              userId: c.user_id,

              name: user?.name || 'Client Representative',

              email: user?.email || '',

              role: 'CLIENT_REP',

              stakeholderRole: 'CLIENT',

              designation: user?.designation || 'Client Representative',

              assignedAt: c.assigned_at,

            };

          });



        return {

          contractId,

          team: {

            CLIENT: clientReps,

            MAIN_CONTRACTOR: teamAssignments,

            OEM: [],

            ONM: [],

          },

        };

      }



      const BASE_URL =

        getApiBase();

      const response = await fetch(`${BASE_URL}/api/v1/contracts/${contractId}/team`);

      if (!response.ok) {

        throw new Error('Failed to fetch contract team');

      }

      return response.json();

    },

    enabled: !!contractId,

  });



  return {

    team: data?.team ?? { CLIENT: [], MAIN_CONTRACTOR: [], OEM: [], ONM: [] },

    isLoading,

    refetch,

    error,

  };

}



export function useContractCandidates(contractId: string, organizationId: string) {

  const { data, isLoading, refetch, error } = useQuery<{ candidates: any[] }>({

    queryKey: ['contractCandidates', contractId, organizationId],

    queryFn: async () => {

      const BASE_URL =

        getApiBase();

      const response = await fetch(

        `${BASE_URL}/api/v1/contracts/${contractId}/candidates?organizationId=${organizationId}`,

      );

      if (!response.ok) {

        throw new Error('Failed to fetch contract candidates');

      }

      return response.json();

    },

    enabled: !!contractId && !!organizationId,

  });



  return {

    candidates: data?.candidates ?? [],

    isLoading,

    refetch,

    error,

  };

}



export function useStakeholderOrganizations() {

  const { data, isLoading, refetch, error } = useQuery<{ stakeholderOrganizations: any[] }>({

    queryKey: ['stakeholderOrganizations'],

    queryFn: async () => {

      const BASE_URL =

        getApiBase();

      const response = await fetch(`${BASE_URL}/api/v1/stakeholders`);

      if (!response.ok) {

        throw new Error('Failed to fetch stakeholder organizations');

      }

      return response.json();

    },

  });



  return {

    organizations: data?.stakeholderOrganizations ?? [],

    isLoading,

    refetch,

    error,

  };

}



export function useContractOrganizationCandidates(

  contractId: string | undefined,

  role?: string,

  searchQuery?: string,

  enabled: boolean = true,

) {

  const trimmedSearch = searchQuery?.trim() || '';

  const { data, isLoading, isFetching, refetch, error } = useQuery<{ candidates: any[] }>({

    queryKey: ['contractOrganizationCandidates', contractId, role, trimmedSearch],

    queryFn: async () => {

      if (!contractId) return { candidates: [] };

      const params = new URLSearchParams();

      if (role) params.set('role', role);

      if (trimmedSearch) params.set('q', trimmedSearch);

      const queryString = params.toString() ? `?${params.toString()}` : '';

      return api.get<{ candidates: any[] }>(

        `/api/v1/contracts/${contractId}/organization-candidates${queryString}`,

      );

    },

    enabled: Boolean(contractId && enabled),

    staleTime: 30000,

  });



  return {

    candidates: data?.candidates ?? [],

    isLoading: isLoading || isFetching,

    refetch,

    error,

  };

}



export function useContractTeamMutations(contractId: string) {

  const queryClient = useQueryClient();



  const addTeamUser = useMutation({

    mutationFn: (payload: {

      membershipId: string;

      contractDesignation: string;

      status?: string;

      projectSiteIds?: string[];

    }) => api.post<any>(`/api/v1/contracts/${contractId}/team/add-user`, payload),

    onSuccess: () => {

      queryClient.invalidateQueries({ queryKey: ['contractTeam', contractId] });

      if (contractId) {

        queryClient.invalidateQueries({ queryKey: ['contractCandidates', contractId] });

      }

    },

  });



  const createInlineOrganization = useMutation({

    mutationFn: (payload: {

      name: string;

      legalName?: string | null;

      countryOfRegistration?: string;

      website?: string | null;

      logoUrl?: string | null;

      primaryContactName: string;

      primaryContactEmail: string;

      primaryContactPhone: string;

      contractStakeholderRole: string;

      equipmentCategories?: string[];

      brandsRepresented?: string[];

      serviceCapabilities?: string[];

      serviceRegions?: string[];

    }) => api.post<any>(`/api/v1/contracts/${contractId}/organizations/inline`, payload),

    onSuccess: () => {

      queryClient.invalidateQueries({ queryKey: ['contractTeam', contractId] });

      queryClient.invalidateQueries({ queryKey: ['contractOrganizationCandidates', contractId] });

    },

  });



  const associateOrganization = useMutation({

    mutationFn: (payload: { stakeholderOrganizationId: string; contractStakeholderRole: string }) =>

      api.post<any>(`/api/v1/contracts/${contractId}/organizations/associate`, payload),

    onSuccess: () => {

      queryClient.invalidateQueries({ queryKey: ['contractTeam', contractId] });

      queryClient.invalidateQueries({ queryKey: ['contractOrganizationCandidates', contractId] });

    },

  });



  const updateOrganizationLink = useMutation({

    mutationFn: ({

      orgId,

      payload,

    }: {

      orgId: string;

      payload: { contractStakeholderRole?: string; primaryContactMembershipId?: string | null };

    }) => api.post<any>(`/api/v1/contracts/${contractId}/organizations/${orgId}/update`, payload),

    onSuccess: () => {

      queryClient.invalidateQueries({ queryKey: ['contractTeam', contractId] });

    },

  });



  const updateTeamUserAssignment = useMutation({

    mutationFn: ({

      assignmentId,

      payload,

    }: {

      assignmentId: string;

      payload: { contractDesignation?: string; status?: string };

    }) => api.post<any>(`/api/v1/contracts/${contractId}/team/${assignmentId}/update`, payload),

    onSuccess: () => {

      queryClient.invalidateQueries({ queryKey: ['contractTeam', contractId] });

    },

  });



  return {

    addTeamUser,

    createInlineOrganization,

    associateOrganization,

    updateOrganizationLink,

    updateTeamUserAssignment,

  };

}



export function useStakeholderOrgMutations() {

  const queryClient = useQueryClient();



  const updateStakeholderOrganization = useMutation({

    mutationFn: ({ id, payload }: { id: string; payload: Record<string, any> }) =>

      api.put<any>(`/api/v1/stakeholders/${id}`, payload),

    onSuccess: () => {

      queryClient.invalidateQueries({ queryKey: ['stakeholderOrganizations'] });

    },

  });



  return {

    updateStakeholderOrganization,

  };

}
